import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../../api/axios';
import StatsCard from '../Common/StatsCard';
import DonutChart from '../Common/DonutChart';

export default function LibrarianDashboard() {
  const [books, setBooks] = useState([]);
  const [classes, setClasses] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    Promise.all([
      api.get('/library/').catch(() => ({ data: [] })),
      api.get('/classes/').catch(() => ({ data: [] })),
      api.get('/announcements/').catch(() => ({ data: [] })),
    ])
      .then(([b, c, a]) => {
        setBooks(b.data || []);
        setClasses(c.data || []);
        setAnnouncements(a.data || []);
      })
      .finally(() => setLoading(false));
  }, []);

  // ─── Derived stats ───
  const totalBooks = books.length;
  const pdfCount = books.filter((b) => b.format === 'PDF').length;
  const wordCount = books.filter((b) => b.format === 'Word').length;
  const linkCount = books.filter((b) => b.format === 'Link').length;
  const ebookCount = books.filter((b) => b.format === 'eBook').length;

  const formatData = [
    { label: 'PDF', value: pdfCount, color: '#ef4444' },
    { label: 'Word', value: wordCount, color: '#3b82f6' },
    { label: 'Link', value: linkCount, color: '#8b5cf6' },
    { label: 'eBook', value: ebookCount, color: '#10b981' },
  ].filter((d) => d.value > 0);

  const recentBooks = [...books]
    .sort((a, b) => (b.id || 0) - (a.id || 0))
    .slice(0, 5);

  // Books per class
  const booksByClass = classes
    .map((c) => ({
      ...c,
      count: books.filter((b) => b.class_id === c.id).length,
    }))
    .filter((c) => c.count > 0)
    .sort((a, b) => b.count - a.count)
    .slice(0, 6);

  const unreadAnnouncements = announcements.filter((a) => !a.is_read).length;

  if (loading) {
    return <div className="bg-white p-8 rounded shadow text-center text-gray-500">Loading…</div>;
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* ═══ Welcome Banner ═══ */}
      <div className="bg-gradient-to-br from-pink-500 via-pink-600 to-rose-600 text-white p-5 sm:p-6 rounded-lg shadow-md relative overflow-hidden">
        <div className="absolute -top-10 -right-10 w-40 h-40 bg-white/10 rounded-full"></div>
        <div className="absolute -bottom-16 -left-10 w-56 h-56 bg-white/5 rounded-full"></div>
        <div className="relative z-10 flex items-center gap-4">
          <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-white/20 backdrop-blur flex items-center justify-center flex-shrink-0">
            <i className="fas fa-book-open text-xl sm:text-2xl"></i>
          </div>
          <div className="min-w-0">
            <h1 className="text-xl sm:text-2xl font-bold truncate">Library Desk</h1>
            <p className="text-pink-100 text-xs sm:text-sm mt-1">
              {totalBooks} {totalBooks === 1 ? 'resource' : 'resources'} across {classes.length} classes
            </p>
          </div>
        </div>
      </div>

      {/* ═══ Stats Grid ═══ */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatsCard label="Total Books" value={totalBooks} icon="fa-book" />
        <StatsCard label="PDFs" value={pdfCount} icon="fa-file-pdf" />
        <StatsCard label="Word Docs" value={wordCount} icon="fa-file-word" />
        <StatsCard label="Links" value={linkCount} icon="fa-link" />
      </div>

      {/* ═══ Quick Actions ═══ */}
      <div className="bg-white p-4 sm:p-5 rounded-lg shadow">
        <h2 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4 flex items-center gap-2">
          <i className="fas fa-bolt text-yellow-500"></i> Quick Actions
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {/* Add Book → opens the Add Book modal directly on the Library page */}
          <button
            onClick={() => navigate('/dashboard/library', { state: { openAddBook: true } })}
            className="bg-pink-500 text-white p-4 rounded-lg text-center hover:bg-pink-600 transition flex flex-col items-center gap-2"
          >
            <i className="fas fa-plus-circle text-xl sm:text-2xl"></i>
            <span className="font-semibold text-sm">Add Book</span>
          </button>

          {/* All Books → normal navigation to the Library page */}
          <Link
            to="/dashboard/library"
            className="bg-purple-500 text-white p-4 rounded-lg text-center hover:bg-purple-600 transition flex flex-col items-center gap-2"
          >
            <i className="fas fa-list text-xl sm:text-2xl"></i>
            <span className="font-semibold text-sm">All Books</span>
          </Link>

          <Link
            to="/dashboard/notifications"
            className="bg-indigo-500 text-white p-4 rounded-lg text-center hover:bg-indigo-600 transition flex flex-col items-center gap-2 relative"
          >
            <i className="fas fa-bell text-xl sm:text-2xl"></i>
            <span className="font-semibold text-sm">Announcements</span>
            {unreadAnnouncements > 0 && (
              <span className="absolute top-2 right-2 bg-red-500 text-white text-[10px] px-2 py-0.5 rounded-full font-bold">
                {unreadAnnouncements}
              </span>
            )}
          </Link>
        </div>
      </div>

      {/* ═══ Charts + Recent ═══ */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Format Distribution */}
        <div className="bg-white p-4 sm:p-5 rounded-lg shadow">
          <h2 className="text-base sm:text-lg font-semibold mb-3 flex items-center gap-2">
            <i className="fas fa-chart-pie text-pink-500"></i> Format Distribution
          </h2>
          {formatData.length === 0 ? (
            <div className="text-center py-8 text-gray-400 text-sm">
              <i className="fas fa-chart-pie text-3xl mb-2"></i>
              <p>No resources yet</p>
            </div>
          ) : (
            <DonutChart data={formatData} size={220} />
          )}
        </div>

        {/* Recent Additions */}
        <div className="bg-white p-4 sm:p-5 rounded-lg shadow">
          <h2 className="text-base sm:text-lg font-semibold mb-3 flex items-center gap-2">
            <i className="fas fa-clock text-blue-500"></i> Recent Additions
          </h2>
          {recentBooks.length === 0 ? (
            <div className="text-center py-8 text-gray-400 text-sm">
              <i className="fas fa-book text-3xl mb-2"></i>
              <p>No books added yet</p>
            </div>
          ) : (
            <div className="space-y-2">
              {recentBooks.map((book) => (
                <div
                  key={book.id}
                  className="flex items-center gap-3 p-2 rounded border border-gray-100 hover:bg-gray-50"
                >
                  <div className="w-9 h-9 rounded bg-pink-100 text-pink-700 flex items-center justify-center flex-shrink-0">
                    <i className="fas fa-book text-sm"></i>
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-sm truncate">{book.title}</p>
                    <p className="text-xs text-gray-500 truncate">
                      {book.author || 'Unknown'} · {book.format}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ═══ Books per Class ═══ */}
      {booksByClass.length > 0 && (
        <div className="bg-white p-4 sm:p-5 rounded-lg shadow">
          <h2 className="text-base sm:text-lg font-semibold mb-3 flex items-center gap-2">
            <i className="fas fa-layer-group text-indigo-500"></i> Top Classes by Resources
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {booksByClass.map((c) => (
              <div key={c.id} className="bg-indigo-50 border border-indigo-100 rounded p-3 text-center">
                <p className="text-xs text-indigo-700 font-medium truncate">{c.name}</p>
                <p className="text-xl font-bold text-indigo-900 mt-1">{c.count}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}